import { useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppDispatch } from 'hooks/reduxHooks'
import { useScreenSizeUp } from 'hooks/useScreenSize'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { setTravelPreferencesModalViewed } from 'actions/LESubscriptionsActions'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import SolidCheckCircleIcon from 'LuxKit/Icons/solid/SolidCheckCircleIcon'
import TravelPreferencesModalMobileInitial from './TravelPreferencesModalSteps/TravelPreferencesModalMobileInitial'
import TravelPreferencesModalHolidayTypes from './TravelPreferencesModalSteps/TravelPreferencesModalHolidayTypes'
import TravelPreferencesModalDestinations from './TravelPreferencesModalSteps/TravelPreferencesModalDestinations'
import TravelPreferencesModalHotelBrands from './TravelPreferencesModalSteps/TravelPreferencesModalHotelBrands'
import TravelPreferencesModalTravellers from './TravelPreferencesModalSteps/TravelPreferencesModalTravellers'
import { ModalSteps } from './defaults'

function TravelPreferencesModal() {
  const intl = useIntl()
  const isTabletScreenSizeAndHigher = useScreenSizeUp('tablet')

  const [hasSavedPreferences, setHasSavedPreferences] = useState<boolean>(false)
  const [step, setStep] = useState<ModalSteps>(isTabletScreenSizeAndHigher ? ModalSteps.holidayTypes : ModalSteps.mobileInitial)
  const showBackButton = step !== ModalSteps.mobileInitial && step !== ModalSteps.holidayTypes

  const dispatch = useAppDispatch()
  const modalContext = useModalElementContext()

  const modalSubtitles: Record<string, string | undefined> = {
    mobileInitial: undefined,
    holidayTypes: intl.formatMessage({ id: 'ZdaJw_ycHhqy0P_r77TJ', defaultMessage: 'Step 1 of 4', description: 'Travel preferences modal - step 1 of 4 subtitle on the travel preferences page' }),
    destinations: intl.formatMessage({ id: 'YlTgw9EiopWZ3bGA2C0r', defaultMessage: 'Step 2 of 4', description: 'Travel preferences modal - step 2 of 4 subtitle on the travel preferences page' }),
    hotelBrands: intl.formatMessage({ id: 'vZDlh0o3BlaA1aRJ-3Ee', defaultMessage: 'Step 3 of 4', description: 'Travel preferences modal - step 3 of 4 subtitle on the travel preferences page' }),
    travellers: intl.formatMessage({ id: 'ytvJbjDBsoj_49XdreoL', defaultMessage: 'Step 4 of 4', description: 'Travel preferences modal - step 4 of 4 subtitle on the travel preferences page' }),
  }

  const wantPersonalisedTitle = intl.formatMessage({
    id: 'wd89nHTw_RvfK0ToeNCQ',
    defaultMessage: 'Want a personalised experience?',
    description: 'Title for travel preferences modal on holiday types step (tablet+)',
  })

  const favouriteStaysTitle = intl.formatMessage({
    id: 'eJ3QeLwrayDVztQw3aX8',
    defaultMessage: 'Favourite stays',
    description: 'Title for travel preferences modal on holiday types step (mobile)',
  })

  const title = useMemo(() => {
    switch (step) {
      case ModalSteps.mobileInitial: return ''
      case ModalSteps.holidayTypes: return isTabletScreenSizeAndHigher ? wantPersonalisedTitle : favouriteStaysTitle
      case ModalSteps.destinations: return intl.formatMessage({ id: 'xozy4DdNTF9z9qMM_5Si', defaultMessage: 'Your destinations', description: 'Title for destinations step in travel preferences modal' })
      case ModalSteps.hotelBrands: return intl.formatMessage({ id: 'uEgban0lLevwZHx1QHP9', defaultMessage: 'Hotel brands', description: 'Title for hotel brands step in travel preferences modal' })
      case ModalSteps.travellers: return intl.formatMessage({ id: 'RQIi2y04K6y73agVkamo', defaultMessage: 'Your travellers', description: 'Title for travellers step in travel preferences modal' })
      default: return ''
    }
  }, [intl, isTabletScreenSizeAndHigher, step, wantPersonalisedTitle, favouriteStaysTitle])

  const snackbarMessage = intl.formatMessage({
    id: 'mSBpu5j2KFKvqn4IXj7C',
    defaultMessage: 'To update these preferences, go to My Account',
    description: 'Success snackbar message after saving travel preferences',
  })

  const snackbarHeading = intl.formatMessage({
    id: 'YW6VVXCO9YUtgT3Okav0',
    defaultMessage: 'Travel preferences saved',
    description: 'Success snackbar heading after saving travel preferences',
  })

  const onShowSuccessSnackbar = useCallback(() => {
    showSnackbar(snackbarMessage, 'success', { heading: snackbarHeading, icon: <SolidCheckCircleIcon /> })
  }, [snackbarMessage, snackbarHeading])

  const onClose = useCallback(() => {
    dispatch(setTravelPreferencesModalViewed())
    modalContext.resolve()

    if (hasSavedPreferences) {
      onShowSuccessSnackbar()
    }
  }, [dispatch, modalContext, hasSavedPreferences, onShowSuccessSnackbar])

  const onSaveLastStep = useCallback(() => {
    dispatch(setTravelPreferencesModalViewed())
    modalContext.resolve()
    onShowSuccessSnackbar()
  }, [dispatch, modalContext, onShowSuccessSnackbar])

  const onSetNextStep = useCallback((step: ModalSteps, isSaveAction?: boolean) => {
    setStep(step)

    if (isSaveAction) {
      setHasSavedPreferences(true)
    }
  }, [])

  const onBack = useCallback(() => {
    if (step === ModalSteps.destinations) return onSetNextStep(ModalSteps.holidayTypes)
    if (step === ModalSteps.hotelBrands) return onSetNextStep(ModalSteps.destinations)
    if (step === ModalSteps.travellers) return onSetNextStep(ModalSteps.hotelBrands)
  }, [onSetNextStep, step])

  return (
    <ModalBase size="L" onClose={onClose} height={step === ModalSteps.mobileInitial ? 'auto' : 'full'} tabletHeight="auto">
      <ModalHeader
        title={title}
        subtitle={modalSubtitles[step]}
        isOverlay={step === ModalSteps.mobileInitial}
        dismissible={step !== ModalSteps.mobileInitial}
        onBackButtonClick={showBackButton ? onBack : undefined}
        onCloseButtonClick={onClose}
      />
      {step === ModalSteps.mobileInitial && <TravelPreferencesModalMobileInitial onSetNextStep={onSetNextStep} onClose={onClose} />}
      {step === ModalSteps.holidayTypes && <TravelPreferencesModalHolidayTypes onSetNextStep={onSetNextStep} />}
      {step === ModalSteps.destinations && <TravelPreferencesModalDestinations onSetNextStep={onSetNextStep} />}
      {step === ModalSteps.hotelBrands && <TravelPreferencesModalHotelBrands onSetNextStep={onSetNextStep} />}
      {step === ModalSteps.travellers && <TravelPreferencesModalTravellers onSaveLastStep={onSaveLastStep} />}
    </ModalBase>
  )
}

export default TravelPreferencesModal
